// src/pages/CaseStudyDetailPage.jsx
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useLocale } from "../contexts/LocaleContext";
import { getCaseStudyItemDetails } from "../api/apiService";
import PageBanner from "../components/common/PageBanner";

const DiagramItem = ({ diagram }) => {
  if (!diagram || !diagram.image?.url) return null;

  const imageUrl = `${diagram.image.url}`;

  return (
    <div className="mb-8 md:mb-12 lg:mb-16 bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
      {diagram.title && (
        <div className="p-4 md:p-6 text-center bg-gray-100 border border-gray-200 bg-opacity-70">
          {" "}
          <h3 className="text-lg md:text-xl font-semibold">{diagram.title}</h3>
        </div>
      )}
      <div className="bg-gray-100 p-2 md:p-4 flex justify-center items-center">
        {" "}
        <img
          src={imageUrl}
          alt={diagram.image.alternativeText || diagram.title || "Diagram"}
          className="max-w-full h-auto object-contain rounded"
        />
      </div>
    </div>
  );
};

function CaseStudyDetailPage() {
  const { caseStudyName } = useParams();
  const { locale } = useLocale();

  const [caseStudyData, setCaseStudyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPageContent = async () => {
      if (!caseStudyName) {
        setError("Case study name not found in URL.");
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const itemData = await getCaseStudyItemDetails(caseStudyName, locale);
        setCaseStudyData(itemData);
        if (!itemData) {
          setError(`Case study details not found for "${caseStudyName}".`);
        }
      } catch (err) {
        console.error(
          `[CaseStudyDetailPage] Failed to fetch data for case study "${caseStudyName}":`,
          err
        );
        setError(err.message || "Failed to load case study content.");
        setCaseStudyData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchPageContent();
  }, [caseStudyName, locale]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[calc(100vh-200px)]">
        <p className="text-xl text-gray-500">Loading...</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="container mx-auto p-4 text-center">
        <p className="text-red-500 text-xl">Error: {error}</p>
      </div>
    );
  }
  if (!caseStudyData) {
    const message = error
      ? error
      : `Case Study "${caseStudyName}" not found or content is unavailable.`;
    return (
      <div className="container mx-auto p-4 text-center">
        <p className="text-gray-700 text-xl">{message}</p>
      </div>
    );
  }

  const { banner, diagrams } = caseStudyData;

  return (
    <div className="bg-gray-100 min-h-screen">
      {" "}
      <PageBanner
        title={banner?.title}
        imageUrl={banner?.image?.url}
        titleColorIsWhite={banner?.titleColorIsWhite ?? true}
        bannerHeightClass="h-52 sm:h-60 md:h-72 lg:h-80"
      />
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16 max-w-4xl">
        {" "}
        {diagrams && diagrams.length > 0 ? (
          diagrams.map((diagram) => (
            <DiagramItem key={diagram.id} diagram={diagram} />
          ))
        ) : (
          <p className="text-center text-gray-600 text-lg">
            No diagrams available for this case study.
          </p>
        )}
      </div>
    </div>
  );
}

export default CaseStudyDetailPage;
